import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Play, Sparkles } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

const GameCard = ({ game }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const navigate = useNavigate();

  const handlePlayClick = () => {
    if (game.playable && game.gameUrl) {
      // Open the game in a new window
      window.open(game.gameUrl, '_blank', 'width=900,height=800');
    } else {
      setShowDialog(true);
    }
  };

  const handleCreateGame = () => {
    setShowDialog(false);
    navigate('/create');
  };

  return (
    <>
      <div
        className="group relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl overflow-hidden border border-white/5 hover:border-purple-500/50 transition-all duration-300 hover:shadow-2xl hover:shadow-purple-500/20 hover:-translate-y-2"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Game Gradient Background with Icon */}
        <div className={`relative aspect-video overflow-hidden bg-gradient-to-br ${game.gradient} flex items-center justify-center`}>
          {game.playable && (
            <div className="absolute top-3 right-3 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full animate-pulse">
              PLAYABLE
            </div>
          )}
          <div className="text-8xl opacity-80 group-hover:scale-110 transition-transform duration-500">
            {game.icon}
          </div>
          
          {/* Overlay on hover */}
          <div className={`absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent transition-opacity duration-300 ${
            isHovered ? 'opacity-100' : 'opacity-0'
          }`}>
            <div className="absolute bottom-4 left-4 right-4 flex gap-2">
              <Button
                size="sm"
                onClick={handlePlayClick}
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
              >
                <Play size={16} className="mr-2" />
                Play Now
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleCreateGame}
                className="flex-1 border-purple-500 text-purple-400 hover:bg-purple-500/10"
              >
                <Sparkles size={16} className="mr-2" />
                Create Similar
              </Button>
            </div>
          </div>
        </div>

        {/* Game Info */}
        <div className="p-5">
          <h3 className="text-xl font-bold text-white mb-2 group-hover:text-purple-400 transition-colors">
            {game.title}
          </h3>
          
          {/* Tags */}
          <div className="flex flex-wrap gap-2 mb-3">
            {game.tags.map((tag, index) => (
              <span
                key={index}
                className="text-xs px-2 py-1 bg-purple-500/20 text-purple-300 rounded-full border border-purple-500/30"
              >
                #{tag}
              </span>
            ))}
          </div>

          {/* Description */}
          <p className="text-gray-400 text-sm line-clamp-2">
            {game.description}
          </p>
        </div>
      </div>

      {/* Info Dialog for non-playable demos */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-gradient-to-br from-gray-900 to-gray-800 border-purple-500/50">
          <DialogHeader>
            <DialogTitle className="text-white text-2xl">Game Concept Example</DialogTitle>
            <DialogDescription className="text-gray-400 text-base">
              This is a concept example of the type of game you can create with GameOS. 
              <br /><br />
              <strong className="text-purple-400">Ready to build something similar?</strong> Our AI will generate a complete, playable game for you in minutes based on your description!
              <br /><br />
              Try our demo games (Space Defender, Bubble Pop, Snake Evolution) to see what's possible!
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-4">
            <Button
              onClick={handleCreateGame}
              className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
            >
              <Sparkles className="mr-2" size={18} />
              Create Your Game
            </Button>
            <Button
              onClick={() => setShowDialog(false)}
              variant="outline"
              className="border-purple-500 text-purple-400 hover:bg-purple-500/10"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default GameCard;